import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FEISHU_RAG_RECOMMENDED_PERMISSIONS } from '@/constants/feishu-rag-scopes';

const POPOVER_MAX_WIDTH = 360;
const VIEWPORT_PAD = 8;

export function FeishuRAGPermissionRecommendations() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const updatePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    let left = r.left;
    if (left + POPOVER_MAX_WIDTH + VIEWPORT_PAD > vw) {
      left = Math.max(VIEWPORT_PAD, vw - POPOVER_MAX_WIDTH - VIEWPORT_PAD);
    }
    setPos({ top: r.bottom + 6, left });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    const onMove = () => updatePos();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="inline h-auto min-h-0 p-0 align-baseline text-xs font-normal leading-none text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          updatePos();
          setOpen((v) => !v);
        }}
      >
        权限推荐
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              role="dialog"
              aria-label="飞书应用权限推荐"
              className="fixed z-[200] rounded-md border border-border bg-popover px-3 py-2.5 text-xs text-popover-foreground shadow-lg"
              style={{
                top: pos.top,
                left: pos.left,
                width: `min(${POPOVER_MAX_WIDTH}px, calc(100vw - ${VIEWPORT_PAD * 2}px))`,
              }}
            >
              <p className="text-muted-foreground leading-relaxed">
                在飞书开放平台「权限管理」中为应用机器人开通以下权限；用户 OAuth
                授权时将请求相同范围。
              </p>
              <ul className="mt-2 max-h-[min(320px,50vh)] overflow-y-auto space-y-1.5">
                {FEISHU_RAG_RECOMMENDED_PERMISSIONS.map(({ scope, label }) => (
                  <li
                    key={scope}
                    className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2"
                  >
                    <span className="font-medium text-foreground shrink-0">{label}</span>
                    <code className="text-[11px] text-muted-foreground break-all">{scope}</code>
                  </li>
                ))}
              </ul>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
